import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { CategoryService, Tier, TierAware } from '@shared-data';
import { ActivatedRoute } from '@angular/router';
import { ColumnOptions, MessageService, NamedEntity } from '@shared';
import {
  aLaCarteProductType,
  bundleProductType,
  ExportActiveFilter,
  exportActiveFilterOptions,
  ExportOption,
  ltoProductType,
  pricingProductTypes,
} from '../../constants';
import { BasePricingChangesetComponent } from '../base-pricing-changeset/base-pricing-changeset.component';
import { loadOuterSelection, saveOuterSelection } from '../top-level-filter-cache';

@Component({
  selector: 'app-pricing',
  templateUrl: './pricing.component.html',
  styleUrl: './pricing.component.scss',
})
export class PricingComponent extends TierAware implements OnInit {
  locked: boolean = false;

  restaurantId?: string;

  productTypes = pricingProductTypes;

  _productType!: NamedEntity;

  get productType() {
    return this._productType;
  }
  set productType(v: NamedEntity) {
    this._productType = v;

    // The sub-selector lists bundles instead of categories on the Bundles view
    this.defaultCategory.name = this.isBundleView
      ? 'Bundle (All)'
      : 'Category (All)';

    if (v && this.restaurantId) {
      this.fetchCategories();
    }
  }

  get isBundleView(): boolean {
    return this._productType?.id === bundleProductType.id;
  }

  defaultCategory = new NamedEntity('-1', 'Category (All)');
  selectedCategory: NamedEntity = this.defaultCategory;

  categories: NamedEntity[] = [];

  component?: BasePricingChangesetComponent;

  showLoadRbiRecommendationsConfirmation: boolean = false;
  showDeliverySettings: boolean = false;
  showDeliveryPanel: boolean = false;
  rbiRecommendationsDate?: Date | null = null;
  tier?: Tier;

  pendingExportOption: ExportOption | null = null;
  exportActiveFilter: ExportActiveFilter = 'Active';
  exportActiveFilterOptions = exportActiveFilterOptions;

  constructor(
    injector: Injector,
    private activatedRoute: ActivatedRoute,
    private categoryService: CategoryService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {
    super(injector);
    this.activatedRoute.params.subscribe((params) => {
      const queryParams = this.activatedRoute.snapshot.queryParams;
      if (queryParams['isLto']) {
        this.productType = ltoProductType;
        return;
      }

      const urlType = this.activatedRoute.snapshot.firstChild?.url[0]?.path;
      const cached = loadOuterSelection('pricing');
      const restored = this.productTypes.find(
        (t) => t.id === (urlType ?? cached?.productTypeId),
      );
      this.productType = restored ?? aLaCarteProductType;

      if (cached?.category && cached.productTypeId === this.productType.id) {
        this.selectedCategory = new NamedEntity(
          cached.category.id,
          cached.category.name,
        );
      }
    });
  }

  override ngOnInit(): void {
    this.subscribeToPendingChangesChange();

    super.ngOnInit();
  }

  updatePendingChanges() {
    this.globalEventsService.updatePendingChanges();
  }

  subscribeToPendingChangesChange() {
    this.globalEventsService.pendingChangesUpdated$.subscribe(() => {
      this.updateParameters();
    });
  }

  override afterTierChange(tier: Tier): void {
    if (tier) {
      this.tier = tier;
      this.restaurantId = tier.kingRestaurantId;
      this.rbiRecommendationsDate = tier.kingRestaurantLastRbiPriceUpdate;
      this.locked = tier.isLocked ?? false;
      this.fetchCategories();
    }
  }

  updateParameters() {
    saveOuterSelection('pricing', {
      productTypeId: this.productType.id,
      category:
        this.selectedCategory.id !== '-1'
          ? { id: this.selectedCategory.id, name: this.selectedCategory.name }
          : null,
    });

    const queryParams = {
      restaurantId: this.restaurantId,
      locked: this.locked,
      category:
        this.selectedCategory.id !== '-1' ? this.selectedCategory.id : null,
      _refresh: Date.now(),
    };
    this.router.navigate([this.productType.id], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  fetchCategories() {
    this.categoryService
      .getCategories(this.restaurantId ?? '', this.productType.id)
      .subscribe({
        next: (response) => {
          this.categories = response.data;
          if (this.selectedCategory.id !== '-1') {
            //restore selected category if exists
            const cat = this.categories.find(
              (c) =>
                c.id === this.selectedCategory.id ||
                c.name === this.selectedCategory.name,
            );
            this.selectedCategory = cat ?? this.defaultCategory;
          }

          this.updateParameters();
        },
        error: (err) => {
          this.categories = [];
          this.selectedCategory = this.defaultCategory;
          this.updateParameters();
          this.messageService.error(err);
        },
      });
  }

  onActivate(componentRef: any) {
    this.component = componentRef as BasePricingChangesetComponent;
    this.cdr.detectChanges();
  }

  saveChanges() {
    if (this.component) {
      this.component.saveChanges();
    }
  }

  // The export menu opens the dialog first, so the user picks which items to include
  exportToExcel(exportOption: ExportOption) {
    this.pendingExportOption = exportOption;
    this.exportActiveFilter = 'Active';
  }

  confirmExport() {
    if (this.component && this.pendingExportOption) {
      this.component.exportToExcel(
        this.pendingExportOption,
        this.exportActiveFilter,
      );
    }
    this.pendingExportOption = null;
  }

  updateFontSize(fontSize: number) {
    if (this.component) {
      this.component.fontSize = fontSize;
    }
  }

  loadRbiRecommendations() {
    if (this.component) {
      this.component.loadRbiRecommendations();
    }
  }

  onBulkDeliveryApplied() {
    this.showDeliveryPanel = false;
    this.updateParameters();
    this.updatePendingChanges();
  }
}
